import {Component, EventEmitter, Input, OnDestroy, Output} from '@angular/core';
import {DialogCloseResult, DialogRef, DialogService} from '@progress/kendo-angular-dialog';
import {Observable, of} from 'rxjs';
import {map} from 'rxjs/operators';
import {OverrideType} from '../../shared/enums';
import {OverrideRequest, OverrideResponse} from '../../shared/override.model';

@Component({
  selector: 'app-forecast-override',
  templateUrl: './forecast-override.component.html',
  styleUrls: ['./forecast-override.component.scss']
})
export class ForecastOverrideComponent implements OnDestroy {
  public active = false;
  public overrideObject;
  public overrideType: OverrideType = OverrideType.Single;
  public title: string;
  public overridePct: 0;
  public dmdOverride: 0;
  private selectedRowCount = 0;
  private overriddenRowCount = 0;
  private confirmDialog: DialogRef;

  @Input() public set dataItem(dataItem: OverrideRequest) {
      this.active = dataItem !== undefined;
      if (this.active) {
          this.overrideObject = dataItem.data;
          this.overrideType = dataItem.overrideType;
          this.dmdOverride = dataItem.data.overrideDemand;
          this.selectedRowCount = dataItem.selectedRowCount || 0;
          this.overriddenRowCount = dataItem.overriddenRowCount || 0;
          this.setTitle();
      }
  }

  @Output() save: EventEmitter<OverrideResponse> = new EventEmitter();
  @Output() cancel: EventEmitter<OverrideResponse> = new EventEmitter();
  @Output() revert: EventEmitter<OverrideResponse> = new EventEmitter();

  constructor(private dialogService: DialogService) {
  }

  ngOnDestroy(): void {
      // Otherwise a confirmation left open outlives the component.
      this.confirmDialog?.close();
  }

  public onSave(event): void {
      event.preventDefault();
      this.confirmBulkChange('Override').subscribe((confirmed: boolean) => {
          if (!confirmed) {
              return;
          }
          this.save.emit({
              overrideType: this.overrideType,
              data: [this.overrideObject],
              overridePercent: (this.overridePct / 100),
              overrideDemand: this.dmdOverride
          });
          this.clear();
      });
  }

  public onCancel(event): void {
      event.preventDefault();
      this.closeDialog();
  }

  public closeDialog(): void {
      this.clear();
      this.cancel.emit();
  }

  public onRevert(event): void {
      event.preventDefault();
      this.confirmBulkChange('Revert').subscribe((confirmed: boolean) => {
          if (!confirmed) {
              return;
          }
          this.revert.emit({
              overrideType: this.overrideType,
              data: [this.overrideObject]
          });
          this.clear();
      });
  }

  public checkOverrideType(overrideType: any): boolean {
        return overrideType === this.overrideType;
  }

  public onPercentOverrideChange(input: any): void {
      if (input >= 0 || input <= 0) {
          const pct = input / 100;
          this.dmdOverride = (this.overrideObject.totalForecastDemand * pct) +
              this.overrideObject.totalForecastDemand;
      }
  }

  public bulkInputChange(isPctInput: boolean): void {
      if (isPctInput) {
          if (this.dmdOverride > 0 || this.dmdOverride < 0) {
              this.dmdOverride = 0;
          }
      } else {
          if (this.overridePct > 0 || this.overridePct < 0) {
              this.overridePct = 0;
          }
      }
  }

  // A bulk change fans out across every selected grid row, so state the scope and
  // make the user confirm it before anything is sent. Single overrides pass through.
  private confirmBulkChange(actionLabel: string): Observable<boolean> {
      if (this.overrideType !== OverrideType.Bulk) {
          return of(true);
      }
      // Revert only touches rows that already carry an override.
      const rowCount = actionLabel === 'Revert' ? this.overriddenRowCount : this.selectedRowCount;
      if (!rowCount) {
          // The confirm lands in the page-level kendoDialogContainer, which sits after
          // this inline dialog in the DOM, so it paints above it at the same z-index.
          this.confirmDialog = this.dialogService.open({
              title: `Nothing to ${actionLabel}`,
              content: actionLabel === 'Revert'
                  ? 'None of the selected rows has a forecast override to revert.'
                  : 'Select at least one row in the grid before applying a bulk override.',
              actions: [{text: 'OK', primary: true}]
          });
          return this.confirmDialog.result.pipe(map(() => false));
      }

      const confirmLabel = `Yes, ${actionLabel}`;
      this.confirmDialog = this.dialogService.open({
          title: `Confirm Bulk ${actionLabel}`,
          content: this.buildConfirmationMessage(actionLabel),
          actions: [
              {text: 'No'},
              {text: confirmLabel, primary: true}
          ]
      });
      return this.confirmDialog.result.pipe(
          map((result: any) => !(result instanceof DialogCloseResult) && result?.text === confirmLabel)
      );
  }

  private buildConfirmationMessage(actionLabel: string): string {
      if (actionLabel === 'Revert') {
          const count = this.overriddenRowCount;
          return `Revert the forecast override on ${count} of the ${this.selectedRowCount} `
              + `selected journey/date combination${this.selectedRowCount === 1 ? '' : 's'}?`
              + ' Rows without an existing override are not affected.';
      }
      return `Apply this demand override to ${this.selectedRowCount} selected `
          + `journey/date combination${this.selectedRowCount === 1 ? '' : 's'}?`;
  }

  private setTitle(): void {
      if (this.overrideType === OverrideType.Bulk) {
          this.title = 'Bulk Demand Overrides';
      } else {
          this.title = 'Demand Overrides';
      }
  }

  private clear(): void {
      this.active = false;
      this.overrideObject = null;
      this.overridePct = 0;
      this.dmdOverride = 0;
  }

}
